import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiServices from "../../service";
import { RootState } from "../store";

/* ================= Types ================= */

export interface ExtractItem {
  SearchId: string;
  Title: string;
  Description: string;
  Condition: string;
  UserId: string | null;
  Type: "Condition" | "Export" | "Selection";
}

interface ExtractState {
  extracts: ExtractItem[];
  count: number;
  loading: boolean;
  error: string | null;
}

const initialState: ExtractState = {
  extracts: [],
  count: 0,
  loading: false,
  error: null,
};

/* ================= Async Thunk ================= */

export const fetchExtracts = createAsyncThunk<
  { data: ExtractItem[]; count: number },
  void,
  { rejectValue: string }
>("extract/fetchExtracts", async (_, { rejectWithValue }) => {
  try {
    const response = await apiServices.extract.getExtract();

    return {
      data: response.data?.data ?? [],
      count: response.data?.count ?? 0,
    };
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch extracts",
    );
  }
});

/* ================= Slice ================= */

const extractSlice = createSlice({
  name: "extract",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchExtracts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExtracts.fulfilled, (state, action) => {
        state.loading = false;
        state.extracts = action.payload.data;
        state.count = action.payload.count;
      })
      .addCase(fetchExtracts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Something went wrong";
      });
  },
});

/* ================= Selectors ================= */

export const selectAllExtracts = (state: RootState) => state.extract.extracts;

export const selectConditionExtracts = (state: RootState) =>
  state.extract.extracts.filter((e) => e.Type === "Condition");

export const selectSelectionExtracts = (state: RootState) =>
  state.extract.extracts.filter((e) => e.Type === "Selection");

export const selectExportExtracts = (state: RootState) =>
  state.extract.extracts.filter((e) => e.Type === "Export");

export default extractSlice.reducer;
